import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Clock, Shield, Wallet, PiggyBank, Target, Zap,
  Info, ChevronRight, Lightbulb, BarChart3, Home
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Cell, ReferenceLine, Area, AreaChart,
  ComposedChart
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Freedom KPIs Module
 * Read-only visualization and analytics layer
 * Pulls all data from My Numbers and Forecast modules
 */

// Color palette
const COLORS = {
  red: "#DC2626",
  yellow: "#F59E0B", 
  lightGreen: "#22C55E",
  darkGreen: "#166534",
  blue: "#3B82F6",
  purple: "#8B5CF6",
  gray: "#6B7280",
  dark: "#111827",
};

// Utility functions
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "$0";
  const amount = Math.round(value);
  if (Math.abs(amount) >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toLocaleString()}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(1)}%`;
};

// Get color based on months of independence
const getMonthsColor = (months) => {
  if (months < 3) return COLORS.red;
  if (months < 12) return COLORS.yellow;
  if (months < 36) return COLORS.lightGreen;
  return COLORS.darkGreen;
};

// Get status label
const getMonthsStatus = (months) => {
  if (months < 3) return { label: "Critical", color: "text-red-500" };
  if (months < 12) return { label: "Building", color: "text-yellow-500" };
  if (months < 36) return { label: "Stable", color: "text-green-500" };
  return { label: "Free", color: "text-emerald-400" };
};

// Tooltip component for explanations
const InfoTooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <Info className="w-3.5 h-3.5 text-gray-500 cursor-help inline" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 bg-gray-800 text-gray-200 text-xs rounded-lg shadow-xl -top-2 left-6 border border-gray-700">
      {text}
    </div>
  </div>
);

// Helper to format months/years
const formatTimeIndependence = (months) => {
  if (months >= 12) {
    return { value: (months / 12).toFixed(1), unit: "years" };
  }
  return { value: months.toFixed(1), unit: "months" };
};

// Section 1: Time Freedom - Months of Independence
const MonthsOfIndependence = ({ data }) => {
  const { liquidAssets, basicExpenses, regularExpenses, debtPayments, luxuryExpenses, totalExpenses } = data;
  
  const monthsBasic = basicExpenses > 0 ? liquidAssets / basicExpenses : 0;
  const monthsBasicRegular = (basicExpenses + regularExpenses) > 0 ? liquidAssets / (basicExpenses + regularExpenses) : 0;
  const monthsBasicRegularDebt = (basicExpenses + regularExpenses + debtPayments) > 0 
    ? liquidAssets / (basicExpenses + regularExpenses + debtPayments) : 0;
  const monthsFull = totalExpenses > 0 ? liquidAssets / totalExpenses : 0;
  
  // Format for display - show years if > 12 months
  const formatTime = (months) => {
    if (months >= 12) {
      return { value: (months / 12).toFixed(1), unit: "y" };
    }
    return { value: months.toFixed(1), unit: "m" };
  };
  
  const segmentData = [
    { name: "Basic Only", months: monthsBasic, formatted: formatTime(monthsBasic), color: COLORS.blue },
    { name: "Basic + Regular", months: monthsBasicRegular, formatted: formatTime(monthsBasicRegular), color: COLORS.purple },
    { name: "Basic + Reg + Debt", months: monthsBasicRegularDebt, formatted: formatTime(monthsBasicRegularDebt), color: COLORS.lightGreen },
    { name: "Full Lifestyle", months: monthsFull, formatted: formatTime(monthsFull), color: COLORS.darkGreen },
  ];
  
  const mainMonths = monthsFull;
  const mainFormatted = formatTime(mainMonths);
  const status = getMonthsStatus(mainMonths);
  
  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            <CardTitle className="text-white text-lg">Time Independence</CardTitle>
            <InfoTooltip text="How long you can maintain your lifestyle without any income, using only liquid productive assets." />
          </div>
          <span className={`text-sm font-semibold px-2 py-1 rounded ${status.color} bg-gray-800`}>
            {status.label}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {/* Main KPI Display */}
        <div className="text-center mb-6">
          <div className="text-5xl font-bold text-white mb-1">
            {mainFormatted.value}
            <span className="text-2xl text-gray-400 ml-1">{mainFormatted.unit === "y" ? "years" : "months"}</span>
          </div>
          <div className="text-gray-400 text-sm">of full lifestyle coverage</div>
        </div>
        
        {/* Segmented Bar */}
        <div className="mb-6">
          <div className="h-8 bg-gray-800 rounded-lg overflow-hidden flex">
            {/* Red zone: 0-3 */}
            <div className="h-full bg-red-600/30 border-r border-gray-700" style={{ width: '5%' }}>
              <div 
                className="h-full bg-red-500 transition-all duration-500"
                style={{ width: `${Math.min(mainMonths / 3 * 100, 100)}%` }}
              />
            </div>
            {/* Yellow zone: 3-12 */}
            <div className="h-full bg-yellow-600/30 border-r border-gray-700" style={{ width: '15%' }}>
              <div 
                className="h-full bg-yellow-500 transition-all duration-500"
                style={{ width: `${mainMonths > 3 ? Math.min((mainMonths - 3) / 9 * 100, 100) : 0}%` }}
              />
            </div>
            {/* Light Green zone: 12-36 */}
            <div className="h-full bg-green-600/30 border-r border-gray-700" style={{ width: '40%' }}>
              <div 
                className="h-full bg-green-500 transition-all duration-500"
                style={{ width: `${mainMonths > 12 ? Math.min((mainMonths - 12) / 24 * 100, 100) : 0}%` }}
              />
            </div>
            {/* Dark Green zone: 36+ */}
            <div className="h-full bg-emerald-600/30" style={{ width: '40%' }}>
              <div 
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${mainMonths > 36 ? Math.min((mainMonths - 36) / 24 * 100, 100) : 0}%` }}
              />
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0</span>
            <span>3m</span>
            <span>12m</span>
            <span>36m</span>
            <span>60m+</span>
          </div>
        </div>
        
        {/* Breakdown */}
        <div className="grid grid-cols-2 gap-3">
          {segmentData.map((item, idx) => (
            <div key={idx} className="bg-gray-800/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">{item.name}</div>
              <div className="text-xl font-bold" style={{ color: item.color }}>
                {item.formatted.value} <span className="text-sm font-normal text-gray-500">{item.formatted.unit === "y" ? "years" : "months"}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Section 2: Passive Income Coverage with Bar Chart
const PassiveCoverageLadder = ({ data }) => {
  const { passiveIncome, basicExpenses, regularExpenses, debtPayments, luxuryExpenses } = data;
  
  // Calculate cumulative thresholds
  const basicThreshold = basicExpenses;
  const regularThreshold = basicExpenses + regularExpenses;
  const debtThreshold = basicExpenses + regularExpenses + debtPayments;
  const luxuryThreshold = basicExpenses + regularExpenses + debtPayments + luxuryExpenses;
  
  // Calculate percentages of total
  const getPercent = (amount) => luxuryThreshold > 0 ? (amount / luxuryThreshold * 100).toFixed(0) : 0;
  
  // Determine bar color based on coverage level
  const getBarColor = () => {
    if (passiveIncome >= debtThreshold) return "#22C55E"; // Green - covering luxury
    if (passiveIncome >= regularThreshold) return "#F97316"; // Orange - covering debt
    if (passiveIncome >= basicThreshold) return "#F59E0B"; // Yellow - covering regular
    return "#DC2626"; // Red - only basic (partial or full)
  };
  
  // Determine current coverage level label
  const getCoverageLevel = () => {
    if (passiveIncome >= luxuryThreshold) return { level: "Full Freedom", color: "text-green-400" };
    if (passiveIncome >= debtThreshold) return { level: "Covering Luxury", color: "text-green-400" };
    if (passiveIncome >= regularThreshold) return { level: "Covering Debt", color: "text-orange-400" };
    if (passiveIncome >= basicThreshold) return { level: "Covering Regular", color: "text-yellow-400" };
    return { level: "Covering Basic", color: "text-red-400" };
  };
  
  const barColor = getBarColor();
  const coverageLevel = getCoverageLevel();
  
  // Total coverage percentage
  const totalCoverage = luxuryThreshold > 0 ? (passiveIncome / luxuryThreshold) * 100 : 0;
  
  // Threshold data for display
  const thresholds = [
    { name: "Basic", amount: basicThreshold, percent: getPercent(basicThreshold), color: "#DC2626" },
    { name: "Regular", amount: regularThreshold, percent: getPercent(regularThreshold), color: "#F59E0B" },
    { name: "Debt", amount: debtThreshold, percent: getPercent(debtThreshold), color: "#F97316" },
    { name: "Luxury", amount: luxuryThreshold, percent: getPercent(luxuryThreshold), color: "#22C55E" },
  ];

  // Calculate bar height percentage
  const barHeightPercent = Math.min((passiveIncome / luxuryThreshold) * 100, 100);

  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-400" />
          <CardTitle className="text-white text-lg">Passive Income Coverage</CardTitle>
          <InfoTooltip text="Bar shows your passive income against expense thresholds. Color indicates current coverage level: Red (Basic), Yellow (Regular), Orange (Debt), Green (Luxury)." />
        </div>
      </CardHeader>
      <CardContent>
        {/* Header Stats */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-3xl font-bold" style={{ color: barColor }}>{totalCoverage.toFixed(0)}%</div>
            <div className={`text-sm ${coverageLevel.color}`}>{coverageLevel.level}</div>
          </div>
          <div className="text-right">
            <div className="text-gray-400 text-xs">Passive Income</div>
            <div className="text-white font-bold text-xl">{formatCurrency(passiveIncome)}</div>
            <div className="text-gray-500 text-xs">/month</div>
          </div>
        </div>
        
        {/* Medium Width Bar Chart */}
        <div className="flex items-end gap-4 justify-center">
          {/* Y-Axis Labels */}
          <div className="flex flex-col justify-between text-right text-xs text-gray-500" style={{ height: '180px' }}>
            <span>{formatCurrency(luxuryThreshold)}</span>
            <span>{formatCurrency(debtThreshold)}</span>
            <span>{formatCurrency(regularThreshold)}</span>
            <span>{formatCurrency(basicThreshold)}</span>
            <span>$0</span>
          </div>
          
          {/* Chart Area - Medium Width */}
          <div className="relative bg-gray-800/50 rounded-lg" style={{ width: '140px', height: '180px' }}>
            {/* Threshold Lines */}
            {thresholds.map((t, idx) => {
              const bottomPercent = (t.amount / luxuryThreshold) * 100;
              return (
                <div 
                  key={idx}
                  className="absolute left-0 right-0 border-t-2 border-dashed"
                  style={{ 
                    bottom: `${bottomPercent}%`,
                    borderColor: t.color,
                  }}
                />
              );
            })}
            
            {/* Passive Income Bar */}
            <div 
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 rounded-t transition-all duration-700"
              style={{ 
                height: `${Math.max(barHeightPercent, 2)}%`,
                backgroundColor: barColor,
                boxShadow: `0 0 15px ${barColor}50`
              }}
            />
          </div>
          
          {/* Threshold Labels */}
          <div className="flex flex-col justify-between text-left text-xs" style={{ height: '180px' }}>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#22C55E" }} />
              <span className="text-gray-300">Luxury</span>
              <span className="text-gray-500">({getPercent(luxuryThreshold)}%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#F97316" }} />
              <span className="text-gray-300">Debt</span>
              <span className="text-gray-500">({getPercent(debtThreshold)}%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#F59E0B" }} />
              <span className="text-gray-300">Regular</span>
              <span className="text-gray-500">({getPercent(regularThreshold)}%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#DC2626" }} />
              <span className="text-gray-300">Basic</span>
              <span className="text-gray-500">({getPercent(basicThreshold)}%)</span>
            </div>
            <span></span>
          </div>
        </div>
        
        {/* Current Value Label */}
        <div className="text-center mt-4 text-sm">
          <span className="text-gray-400">Current: </span>
          <span className="font-bold" style={{ color: barColor }}>{formatCurrency(passiveIncome)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Section 3: Income Structure Quality
const IncomeStructure = ({ data }) => {
  const { activeIncome, passiveIncome, incomeSources, largestSourcePercent } = data;
  
  const totalIncome = activeIncome + passiveIncome;
  const activeRatio = totalIncome > 0 ? (activeIncome / totalIncome) * 100 : 100;
  const passiveRatio = totalIncome > 0 ? (passiveIncome / totalIncome) * 100 : 0;
  
  const isConcentrated = largestSourcePercent > 70;
  const activePassiveRatio = passiveIncome > 0 ? activeIncome / passiveIncome : Infinity;
  
  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <CardTitle className="text-white text-lg">Income Structure</CardTitle>
          <InfoTooltip text="Analyzes your income diversification and active-to-passive ratio. Lower concentration and higher passive income indicate better financial resilience." />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Income Sources */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Income Sources</span>
            <span className="text-white font-bold text-xl">{incomeSources || 1}</span>
          </div>
          
          {/* Concentration Warning */}
          <div className={`flex items-center gap-2 p-2 rounded ${isConcentrated ? 'bg-red-900/30' : 'bg-green-900/30'}`}>
            {isConcentrated ? (
              <AlertTriangle className="w-4 h-4 text-red-400" />
            ) : (
              <CheckCircle className="w-4 h-4 text-green-400" />
            )}
            <span className={`text-sm ${isConcentrated ? 'text-red-400' : 'text-green-400'}`}>
              {largestSourcePercent.toFixed(0)}% from largest source
              {isConcentrated && " - High concentration risk!"}
            </span>
          </div>
        </div>
        
        {/* Active vs Passive */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="text-gray-400 text-sm mb-3">Active vs Passive Income</div>
          
          {/* Visual Bar */}
          <div className="h-6 bg-gray-700 rounded-full overflow-hidden flex mb-3">
            <div 
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${activeRatio}%` }}
            />
            <div 
              className="h-full bg-purple-500 transition-all duration-500"
              style={{ width: `${passiveRatio}%` }}
            />
          </div>
          
          <div className="flex justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span className="text-gray-300">Active: {formatCurrency(activeIncome)}</span>
              <span className="text-gray-500">({activeRatio.toFixed(0)}%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded" />
              <span className="text-gray-300">Passive: {formatCurrency(passiveIncome)}</span>
              <span className="text-gray-500">({passiveRatio.toFixed(0)}%)</span>
            </div>
          </div>
          
          {/* Ratio */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <span className="text-gray-400 text-xs">Active-to-Passive Ratio: </span>
            <span className="text-white font-bold">
              {activePassiveRatio === Infinity ? "∞" : activePassiveRatio.toFixed(1)}:1
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Section 4: Expense Resilience
const ExpenseResilience = ({ data }) => {
  const { fixedExpenses, variableExpenses, totalExpenses, basicExpenses, incomeGrowth, expenseGrowth } = data;
  
  const rigidityRatio = totalExpenses > 0 ? (fixedExpenses / totalExpenses) * 100 : 0;
  const survivalRatio = totalExpenses > 0 ? (basicExpenses / totalExpenses) * 100 : 0;
  const lifestyleInflation = incomeGrowth - expenseGrowth;
  
  const getRigidityStatus = (ratio) => {
    if (ratio > 70) return { label: "Rigid", color: "text-red-400", bg: "bg-red-900/30" };
    if (ratio > 50) return { label: "Moderate", color: "text-yellow-400", bg: "bg-yellow-900/30" };
    return { label: "Flexible", color: "text-green-400", bg: "bg-green-900/30" };
  };
  
  const rigidityStatus = getRigidityStatus(rigidityRatio);
  
  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-yellow-400" />
          <CardTitle className="text-white text-lg">Expense Resilience</CardTitle>
          <InfoTooltip text="Measures how adaptable your expenses are in a crisis. Lower rigidity and survival threshold mean better flexibility." />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Rigidity Ratio */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-gray-400 text-sm">Financial Rigidity</span>
              <InfoTooltip text="Fixed Expenses ÷ Total Expenses. Lower is better - means you can cut more if needed." />
            </div>
            <span className={`px-2 py-1 rounded text-xs font-medium ${rigidityStatus.color} ${rigidityStatus.bg}`}>
              {rigidityStatus.label}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-white">{rigidityRatio.toFixed(0)}%</span>
            <span className="text-gray-500 text-sm mb-1">fixed expenses</span>
          </div>
          <div className="mt-2 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                rigidityRatio > 70 ? 'bg-red-500' : rigidityRatio > 50 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${rigidityRatio}%` }}
            />
          </div>
        </div>
        
        {/* Survival Threshold */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-gray-400 text-sm">Survival Threshold</span>
              <InfoTooltip text="Basic Expenses ÷ Total Expenses. Your minimum to survive if all else is cut." />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-white">{survivalRatio.toFixed(0)}%</span>
            <span className="text-gray-500 text-sm mb-1">of expenses are essential</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            You could cut up to {(100 - survivalRatio).toFixed(0)}% of expenses in a crisis
          </div>
        </div>
        
        {/* Lifestyle Inflation */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-gray-400 text-sm">Lifestyle Inflation Tracker</span>
              <InfoTooltip text="Income Growth % minus Expense Growth %. Positive means you're getting ahead." />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lifestyleInflation >= 0 ? (
              <TrendingUp className="w-5 h-5 text-green-400" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-400" />
            )}
            <span className={`text-2xl font-bold ${lifestyleInflation >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {lifestyleInflation >= 0 ? '+' : ''}{lifestyleInflation.toFixed(1)}%
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Income: +{incomeGrowth}%/yr vs Expenses: +{expenseGrowth}%/yr
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Section 5: Wealth Acceleration
const WealthAcceleration = ({ data }) => {
  const { monthlySavings, totalIncome, productiveAssets, totalAssets, netWorth, netWorthLastYear, totalExpenses } = data;
  
  const savingsRate = totalIncome > 0 ? (monthlySavings / totalIncome) * 100 : 0;
  const daysForFuture = (savingsRate / 100) * 30; // Days per month working for future
  
  const investmentProductivity = totalAssets > 0 ? (productiveAssets / totalAssets) * 100 : 0;
  
  const netWorthGrowth = netWorthLastYear > 0 
    ? ((netWorth - netWorthLastYear) / netWorthLastYear) * 100 
    : netWorth > 0 ? 100 : 0;
  
  const yearsOfExpenses = totalExpenses > 0 ? netWorth / (totalExpenses * 12) : 0;
  
  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-emerald-400" />
          <CardTitle className="text-white text-lg">Wealth Acceleration</CardTitle>
          <InfoTooltip text="Key metrics showing how fast you're building wealth and financial freedom." />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Savings Rate */}
        <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/20 rounded-lg p-4 border border-emerald-700/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-emerald-300 text-sm">Savings Rate</span>
            <PiggyBank className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-4xl font-bold text-white mb-1">{savingsRate.toFixed(1)}%</div>
          <div className="text-emerald-400 text-sm">
            {daysForFuture.toFixed(1)} days/month working for your future
          </div>
          <div className="mt-2 text-xs text-gray-400">
            Saving {formatCurrency(monthlySavings)} of {formatCurrency(totalIncome)} monthly
          </div>
        </div>
        
        {/* Investment Productivity */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-gray-400 text-sm">Investment Productivity</span>
              <InfoTooltip text="Productive Assets ÷ Total Assets. Higher means more of your assets are working for you." />
            </div>
          </div>
          <div className="flex items-end gap-2 mb-2">
            <span className="text-3xl font-bold text-blue-400">{investmentProductivity.toFixed(0)}%</span>
            <span className="text-gray-500 text-sm mb-1">productive</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${investmentProductivity}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {formatCurrency(productiveAssets)} productive of {formatCurrency(totalAssets)} total
          </div>
        </div>
        
        {/* Net Worth Velocity */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Net Worth Growth (YoY)</span>
            {netWorthGrowth >= 0 ? (
              <TrendingUp className="w-5 h-5 text-green-400" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-400" />
            )}
          </div>
          <div className={`text-3xl font-bold ${netWorthGrowth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {netWorthGrowth >= 0 ? '+' : ''}{netWorthGrowth.toFixed(1)}%
          </div>
          <div className="mt-2 pt-2 border-t border-gray-700">
            <span className="text-gray-400 text-sm">Net worth covers </span>
            <span className="text-white font-bold">{yearsOfExpenses.toFixed(1)} years</span>
            <span className="text-gray-400 text-sm"> of expenses</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Freedom Timeline - Time to Financial Freedom
const FreedomProjection = ({ data, assumptions }) => {
  const { passiveIncome, basicExpenses, regularExpenses, debtPayments, luxuryExpenses, monthlySavings, productiveAssets } = data;
  const { passiveGrowth, investmentReturn } = assumptions;
  
  // Total monthly expenses including debt
  const totalMonthlyNeed = basicExpenses + regularExpenses + debtPayments + luxuryExpenses;
  
  // Current coverage percentage
  const currentCoverage = totalMonthlyNeed > 0 ? (passiveIncome / totalMonthlyNeed) * 100 : 0;
  
  // Calculate years to financial freedom (with decimal precision)
  // Financial freedom = passive income >= all expenses + debt payments
  const calculateYearsToFreedom = () => {
    if (passiveIncome >= totalMonthlyNeed) return 0; // Already free!
    if (passiveGrowth <= 0 && monthlySavings <= 0) return Infinity; // No growth path
    
    let months = 0;
    let projectedPassive = passiveIncome;
    let projectedAssets = productiveAssets || 0;
    
    // Monthly rates
    const monthlyPassiveGrowthRate = Math.pow(1 + passiveGrowth / 100, 1/12) - 1;
    const monthlyInvestmentReturn = investmentReturn / 12 / 100;
    
    while (projectedPassive < totalMonthlyNeed && months < 1200) { // 100 years max
      // Add monthly savings to assets
      projectedAssets += monthlySavings;
      // Assets grow by monthly investment return
      projectedAssets *= (1 + monthlyInvestmentReturn);
      // Passive income grows monthly + new income from invested savings (4% annual / 12)
      const newPassiveFromSavings = monthlySavings * 0.04 / 12;
      projectedPassive = projectedPassive * (1 + monthlyPassiveGrowthRate) + newPassiveFromSavings;
      months++;
    }
    
    return months >= 1200 ? Infinity : months / 12; // Convert to years with decimal
  };
  
  const yearsToFreedom = calculateYearsToFreedom();
  
  // Generate projection data for chart
  const projectionData = [];
  let projectedPassive = passiveIncome;
  let projectedAssets = productiveAssets || 0;
  const maxYears = Math.min(yearsToFreedom === Infinity ? 30 : yearsToFreedom + 5, 30);
  
  for (let year = 0; year <= maxYears; year++) {
    projectionData.push({
      year: year,
      passive: projectedPassive,
      target: totalMonthlyNeed
    });
    
    // Project next year
    projectedAssets += (monthlySavings * 12);
    projectedAssets *= (1 + investmentReturn / 100);
    const newPassiveFromSavings = (monthlySavings * 12) * 0.04 / 12;
    projectedPassive = projectedPassive * (1 + passiveGrowth / 100) + newPassiveFromSavings;
  }
  
  // Status based on years to freedom
  const getStatus = (years) => {
    if (years === 0) return { label: "Achieved!", color: "text-emerald-400", bg: "bg-emerald-500" };
    if (years <= 5) return { label: "Very Close", color: "text-green-400", bg: "bg-green-500" };
    if (years <= 10) return { label: "On Track", color: "text-blue-400", bg: "bg-blue-500" };
    if (years <= 20) return { label: "Building", color: "text-yellow-400", bg: "bg-yellow-500" };
    if (years <= 30) return { label: "Long Road", color: "text-orange-400", bg: "bg-orange-500" };
    return { label: "Needs Plan", color: "text-red-400", bg: "bg-red-500" };
  };
  
  const status = getStatus(yearsToFreedom);
  
  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          <CardTitle className="text-white text-lg">Time to Financial Freedom</CardTitle>
          <InfoTooltip text="Financial freedom = when your passive income covers all expenses and debt payments. Based on your current savings rate and investment returns." />
        </div>
      </CardHeader>
      <CardContent>
        {/* Main Display */}
        <div className="text-center mb-6">
          <div className="text-6xl font-bold text-white mb-2">
            {yearsToFreedom === 0 ? "Now!" : yearsToFreedom === Infinity ? "∞" : yearsToFreedom.toFixed(1)}
          </div>
          <div className="text-gray-400 text-lg">
            {yearsToFreedom === 0 ? "You're financially free!" : yearsToFreedom === Infinity ? "years (no clear path)" : "years to achieve financial freedom"}
          </div>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${status.color} ${status.bg}/20`}>
            {status.label}
          </span>
        </div>
        
        {/* Current vs Target */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-gray-400 text-xs mb-1">Current Passive Income</div>
            <div className="text-2xl font-bold text-purple-400">{formatCurrency(passiveIncome)}</div>
            <div className="text-gray-500 text-xs">/month</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-gray-400 text-xs mb-1">Target (All Expenses + Debt)</div>
            <div className="text-2xl font-bold text-cyan-400">{formatCurrency(totalMonthlyNeed)}</div>
            <div className="text-gray-500 text-xs">/month</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-400">Coverage Progress</span>
            <span className="text-white font-bold">{Math.min(currentCoverage, 100).toFixed(1)}%</span>
          </div>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 transition-all duration-500"
              style={{ width: `${Math.min(currentCoverage, 100)}%` }}
            />
          </div>
        </div>
        
        {/* Projection Chart */}
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={projectionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="passiveGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.5}/>
                  <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="year" 
                tick={{ fill: '#9ca3af', fontSize: 10 }} 
                tickFormatter={(v) => `Y${v}`}
              />
              <YAxis tickFormatter={formatCurrency} tick={{ fill: '#9ca3af', fontSize: 10 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(v) => `Year ${v}`}
              />
              <Area type="monotone" dataKey="passive" stroke={COLORS.purple} fill="url(#passiveGradient)" strokeWidth={2} name="Passive Income" />
              <ReferenceLine y={totalMonthlyNeed} stroke={COLORS.red} strokeDasharray="5 5" label={{ value: "Freedom", fill: COLORS.red, fontSize: 10, position: 'right' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Assumptions Note */}
        <div className="mt-4 pt-3 border-t border-gray-800 text-xs text-gray-500">
          Based on: {passiveGrowth}% passive income growth, {investmentReturn}% investment return, {formatCurrency(monthlySavings)}/mo savings invested at 4% withdrawal rate.
        </div>
      </CardContent>
    </Card>
  );
};

// Circular Gauge Component for score indicators with hover tooltip
const CircularGauge = ({ score, label, weight, icon: Icon, explanation, reasoning }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const radius = 40;
  const strokeWidth = 8;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score, 0), 100);
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  
  // Color based on score thresholds
  const getColor = (score) => {
    if (score <= 20) return { stroke: "#DC2626", text: "text-red-500", bg: "bg-red-500/20", label: "Critical" }; // Red
    if (score <= 50) return { stroke: "#F59E0B", text: "text-yellow-500", bg: "bg-yellow-500/20", label: "Needs Work" }; // Yellow
    return { stroke: "#22C55E", text: "text-green-500", bg: "bg-green-500/20", label: "Good" }; // Green
  };
  
  const colors = getColor(score);
  
  return (
    <div 
      className="flex flex-col items-center relative cursor-pointer"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Hover Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full mb-2 z-50 w-72 p-4 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl transform -translate-x-1/4">
          {/* Arrow */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 border-r border-b border-gray-600 rotate-45"></div>
          
          {/* Header */}
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
            <Icon className="w-5 h-5" style={{ color: colors.stroke }} />
            <span className="text-white font-bold text-sm">{label}</span>
            <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${colors.text} ${colors.bg}`}>
              {colors.label}
            </span>
          </div>
          
          {/* What it means */}
          <div className="mb-3">
            <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-1">What it measures</div>
            <p className="text-gray-200 text-sm leading-relaxed">{explanation}</p>
          </div>
          
          {/* Why this score */}
          <div>
            <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-1">Your score breakdown</div>
            <p className="text-gray-300 text-sm leading-relaxed">{reasoning}</p>
          </div>
          
          {/* Weight indicator */}
          {weight && (
            <div className="mt-3 pt-2 border-t border-gray-700 flex justify-between items-center">
              <span className="text-gray-500 text-xs">Contributes to Freedom Score</span>
              <span className="text-white font-bold text-sm">{weight}</span>
            </div>
          )}
        </div>
      )}
      
      <div className="relative w-24 h-24">
        {/* Background circle */}
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke="#374151"
            strokeWidth={strokeWidth}
            className="opacity-30"
          />
          {/* Progress arc */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-xl font-bold ${colors.text}`}>{score.toFixed(0)}</span>
        </div>
      </div>
      {/* Label */}
      <div className="mt-2 text-center">
        <div className="flex items-center justify-center gap-1">
          <Icon className="w-3 h-3 text-gray-400" />
          <span className="text-xs text-gray-300 font-medium">{label}</span>
        </div>
        {weight && <span className="text-xs text-gray-500">({weight})</span>}
      </div>
    </div>
  );
};

// Composite Freedom Score - Main Score Only with Explanation
const FreedomScore = ({ data, assumptions }) => {
  const { 
    liquidAssets, totalExpenses, passiveIncome, basicExpenses, regularExpenses, 
    debtPayments, luxuryExpenses, monthlySavings, totalIncome, incomeSources, largestSourcePercent 
  } = data;
  
  // Calculate component scores (0-100)
  const monthsOfIndependence = totalExpenses > 0 ? liquidAssets / totalExpenses : 0;
  const independenceScore = Math.min((monthsOfIndependence / 36) * 100, 100);
  
  let totalCoverable = basicExpenses + regularExpenses + debtPayments + luxuryExpenses;
  let covered = Math.min(passiveIncome, totalCoverable);
  const passiveCoverageScore = totalCoverable > 0 ? (covered / totalCoverable) * 100 : 0;
  
  const savingsRate = totalIncome > 0 ? (monthlySavings / totalIncome) : 0;
  const savingsScore = Math.min(savingsRate * 100 * 2, 100);
  
  const concentrationPenalty = largestSourcePercent > 70 ? (largestSourcePercent - 70) : 0;
  const diversificationScore = Math.max(0, 100 - concentrationPenalty * 2 - (incomeSources < 3 ? (3 - incomeSources) * 20 : 0));
  
  // Weighted composite
  const compositeScore = (
    independenceScore * 0.30 +
    passiveCoverageScore * 0.30 +
    savingsScore * 0.20 +
    diversificationScore * 0.20
  );
  
  const getScoreStatus = (score) => {
    if (score >= 80) return { label: "Excellent", color: "text-emerald-400", bg: "from-emerald-600 to-green-600", desc: "You're on track for financial freedom!" };
    if (score >= 60) return { label: "Good", color: "text-green-400", bg: "from-green-600 to-lime-600", desc: "Solid progress toward your goals." };
    if (score >= 40) return { label: "Fair", color: "text-yellow-400", bg: "from-yellow-600 to-orange-600", desc: "Building momentum, keep improving." };
    if (score >= 20) return { label: "Needs Work", color: "text-orange-400", bg: "from-orange-600 to-red-600", desc: "Focus on the fundamentals first." };
    return { label: "Critical", color: "text-red-400", bg: "from-red-600 to-red-800", desc: "Immediate action needed." };
  };
  
  const status = getScoreStatus(compositeScore);
  
  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-yellow-400" />
          <CardTitle className="text-white text-lg">Freedom Score</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {/* Main Score */}
        <div className="flex flex-col items-center mb-6">
          <div className={`text-center p-8 rounded-full bg-gradient-to-br ${status.bg} w-36 h-36 flex flex-col items-center justify-center shadow-2xl`}>
            <div className="text-5xl font-bold text-white">{compositeScore.toFixed(0)}</div>
            <div className={`text-sm font-semibold ${status.color}`}>{status.label}</div>
          </div>
          <p className="text-gray-300 text-center mt-4 text-sm">{status.desc}</p>
        </div>
        
        {/* Explanation */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">How it's calculated</div>
          <p className="text-gray-300 text-sm leading-relaxed mb-3">
            Your Freedom Score is a weighted composite of four key financial health metrics:
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Time Independence</span>
              <span className="text-white font-medium">30%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Passive Income Coverage</span>
              <span className="text-white font-medium">30%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Savings Rate</span>
              <span className="text-white font-medium">20%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Income Diversity</span>
              <span className="text-white font-medium">20%</span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-700 text-xs text-gray-500">
            Each metric is scored 0-100 based on your financial data. See the detailed cards below for breakdowns.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// AI Recommendation Box
const AIRecommendation = ({ data, assumptions }) => {
  const { 
    liquidAssets, totalExpenses, passiveIncome, basicExpenses, monthlySavings, 
    totalIncome, largestSourcePercent, incomeSources, productiveAssets, totalAssets,
    fixedExpenses, activeIncome
  } = data;
  
  // Analyze weakest areas
  const monthsOfIndependence = totalExpenses > 0 ? liquidAssets / totalExpenses : 0;
  const savingsRate = totalIncome > 0 ? (monthlySavings / totalIncome) * 100 : 0;
  const passiveCoverage = basicExpenses > 0 ? (passiveIncome / basicExpenses) * 100 : 0;
  const productivityRatio = totalAssets > 0 ? (productiveAssets / totalAssets) * 100 : 0;
  const rigidityRatio = totalExpenses > 0 ? (fixedExpenses / totalExpenses) * 100 : 0;
  
  // Determine highest impact improvement
  let recommendation = {
    title: "Build Emergency Reserve",
    priority: "HIGH",
    impact: "Foundation",
    action: "",
    reasoning: ""
  };
  
  if (monthsOfIndependence < 3) {
    recommendation = {
      title: "Build Emergency Reserve",
      priority: "CRITICAL",
      impact: "Risk Reduction",
      action: `Target ${formatCurrency(totalExpenses * 3)} in liquid assets (3 months of expenses).`,
      reasoning: `You have only ${monthsOfIndependence.toFixed(1)} months of independence. This is dangerously low - one job loss or health issue could be catastrophic.`
    };
  } else if (savingsRate < 20) {
    recommendation = {
      title: "Increase Savings Rate",
      priority: "HIGH",
      impact: "Wealth Velocity",
      action: `Aim for 20% savings rate (currently ${savingsRate.toFixed(0)}%). Find ${formatCurrency((totalIncome * 0.20) - monthlySavings)} more to save monthly.`,
      reasoning: `Your current savings rate of ${savingsRate.toFixed(1)}% is below the recommended 20%. Every 1% increase accelerates your freedom timeline significantly.`
    };
  } else if (passiveCoverage < 50) {
    recommendation = {
      title: "Grow Passive Income",
      priority: "HIGH",
      impact: "Freedom Acceleration",
      action: `Invest more in income-producing assets. Target ${formatCurrency(basicExpenses * 0.5)} monthly passive income.`,
      reasoning: `Passive income covers only ${passiveCoverage.toFixed(0)}% of basic expenses. Building this is the key to financial freedom.`
    };
  } else if (largestSourcePercent > 70) {
    recommendation = {
      title: "Diversify Income Sources",
      priority: "MEDIUM",
      impact: "Risk Reduction",
      action: `Develop a side income stream. Target reducing largest source to under 70% of total income.`,
      reasoning: `${largestSourcePercent.toFixed(0)}% of your income comes from one source. This concentration is risky - diversification protects you.`
    };
  } else if (productivityRatio < 50) {
    recommendation = {
      title: "Increase Asset Productivity",
      priority: "MEDIUM",
      impact: "Passive Growth",
      action: `Move idle cash into income-producing investments. Target 50%+ productive assets.`,
      reasoning: `Only ${productivityRatio.toFixed(0)}% of your assets are working for you. The rest is losing value to inflation.`
    };
  } else {
    recommendation = {
      title: "Optimize & Protect",
      priority: "LOW",
      impact: "Maintenance",
      action: `Review investment allocation quarterly. Consider tax-advantaged accounts.`,
      reasoning: `Your fundamentals are strong. Focus on optimization and protecting what you've built.`
    };
  }
  
  const priorityColors = {
    CRITICAL: "bg-red-600",
    HIGH: "bg-orange-600",
    MEDIUM: "bg-yellow-600",
    LOW: "bg-green-600"
  };
  
  return (
    <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-400" />
          <CardTitle className="text-white text-lg">Highest Impact Improvement</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3 mb-4">
          <span className={`px-2 py-1 rounded text-xs font-bold text-white ${priorityColors[recommendation.priority]}`}>
            {recommendation.priority}
          </span>
          <div>
            <h3 className="text-white font-bold text-lg">{recommendation.title}</h3>
            <span className="text-gray-400 text-sm">Impact: {recommendation.impact}</span>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
          <div className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
            <ChevronRight className="w-4 h-4" />
            Action
          </div>
          <p className="text-gray-300 text-sm">{recommendation.action}</p>
        </div>
        
        <div className="text-gray-400 text-sm">
          <strong className="text-gray-300">Why this matters:</strong> {recommendation.reasoning}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Module Component
const FreedomKPIsModule = ({ myNumbersData: propsMyNumbersData, forecastData }) => {
  const { t } = useTranslation();
  const [localMyNumbers, setLocalMyNumbers] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  
  // Fetch my numbers data directly
  useEffect(() => {
    const fetchMyNumbers = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('simple_token') || sessionStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/my-numbers`, { headers });
        setLocalMyNumbers(response.data);
        setLastFetchTime(Date.now());
      } catch (error) {
        console.error("FreedomKPIs: Error fetching my numbers:", error);
      }
    };
    
    // Initial fetch
    fetchMyNumbers();
    
    // Listen for updates from My Numbers module
    const handleMyNumbersUpdate = (event) => {
      // Small delay to ensure backend has processed the save
      setTimeout(() => {
        fetchMyNumbers();
      }, 100);
    };
    window.addEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    
    return () => {
      window.removeEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    };
  }, []);
  
  // Always prefer locally fetched data since it's refreshed on updates
  // Fall back to props only if local data hasn't been fetched yet
  const myNumbersData = localMyNumbers || propsMyNumbersData;
  
  // Extract and compute data from My Numbers
  const data = useMemo(() => {
    const mn = myNumbersData || {};
    
    // Income
    const activeIncome = mn.monthly_income || 0;
    const passiveIncome = mn.monthly_investment_income || 0;
    const totalIncome = activeIncome + passiveIncome + ((mn.annual_bonus || 0) / 12);
    const incomeSources = mn.income_sources || 1;
    const largestSourcePercent = mn.largest_source_percent || 100;
    
    // Expenses breakdown
    const totalExpenses = mn.monthly_expenses || 0;
    const expenseAllocation = mn.expense_allocation || {};
    const basicPercent = (expenseAllocation.basic_survival || 50) / 100;
    const regularPercent = (expenseAllocation.normal_lifestyle || 30) / 100;
    const luxuryPercent = (expenseAllocation.luxury_emotional || 20) / 100;
    
    const basicExpenses = totalExpenses * basicPercent;
    const regularExpenses = totalExpenses * regularPercent;
    const luxuryExpenses = totalExpenses * luxuryPercent;
    const debtPayments = mn.total_monthly_debt_payments || 0;
    
    // Fixed vs Variable (estimate: basic + debt are mostly fixed)
    const fixedExpenses = basicExpenses + debtPayments;
    const variableExpenses = regularExpenses + luxuryExpenses;
    
    // Assets
    const totalAssets = mn.total_assets || 0;
    const productiveAssets = mn.total_productive_assets || 0;
    const nonProductiveAssets = mn.total_non_productive_assets || 0;
    const productiveAssetsDetail = mn.productive_assets || {};
    const nonProductiveAssetsDetail = mn.non_productive_assets || {};
    
    // Liquid assets for time independence (non-productive liquid + productive)
    const liquidAssets = (nonProductiveAssetsDetail.savings || 0) + 
                        (nonProductiveAssetsDetail.checking || 0) + 
                        (nonProductiveAssetsDetail.cash || 0) + 
                        (nonProductiveAssetsDetail.emergency_fund || 0) +
                        productiveAssets;
    
    // Debt
    const totalDebt = mn.total_debt || 0;
    
    // Net Worth
    const netWorth = totalAssets - totalDebt;
    const netWorthLastYear = mn.net_worth_last_year || netWorth * 0.9; // Estimate if not available
    
    // Savings
    const monthlySavings = totalIncome - totalExpenses - debtPayments;
    
    return {
      activeIncome,
      passiveIncome,
      totalIncome,
      incomeSources,
      largestSourcePercent,
      totalExpenses,
      basicExpenses,
      regularExpenses,
      luxuryExpenses,
      debtPayments,
      fixedExpenses,
      variableExpenses,
      totalAssets,
      liquidAssets,
      productiveAssets,
      totalDebt,
      netWorth,
      netWorthLastYear,
      monthlySavings,
    };
  }, [myNumbersData]);
  
  // Extract assumptions from Forecast
  const assumptions = useMemo(() => {
    const fd = forecastData || {};
    return {
      incomeGrowth: fd.incomeGrowth || 5,
      passiveGrowth: fd.passiveGrowth || fd.investmentReturn || 4,
      expenseGrowth: fd.expensesGrowth || 5,
      investmentReturn: fd.investmentReturn || 4,
      inflation: fd.inflation || 3,
    };
  }, [forecastData]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Target className="w-7 h-7 text-yellow-400" />
            Freedom KPIs
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Real-time financial freedom analytics • Read-only visualization
          </p>
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Data from: My Numbers & Forecast</div>
          <div>Auto-refreshes on changes</div>
        </div>
      </div>
      
      {/* All KPIs in uniform grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Freedom Score */}
        <FreedomScore data={data} assumptions={assumptions} />
        
        {/* AI Recommendation */}
        <AIRecommendation data={data} assumptions={assumptions} />
        
        {/* Time Freedom */}
        <MonthsOfIndependence data={data} />
        
        {/* Passive Coverage */}
        <PassiveCoverageLadder data={data} />
        
        {/* Income Structure */}
        <IncomeStructure data={data} />
        
        {/* Expense Resilience */}
        <ExpenseResilience data={{...data, incomeGrowth: assumptions.incomeGrowth, expenseGrowth: assumptions.expenseGrowth}} />
        
        {/* Wealth Acceleration */}
        <WealthAcceleration data={data} />
        
        {/* Projection Timeline */}
        <FreedomProjection data={data} assumptions={assumptions} />
      </div>
    </div>
  );
};

export default FreedomKPIsModule;
